import { useEffect, useState } from 'react'
import { AlertOctagon, DollarSign, RefreshCw } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

type KeyInfo = {
  ok: boolean
  spend?: number | null
  max_budget?: number | null
  budget_duration?: string | null
  budget_reset_at?: string | null
  blocked?: boolean | null
  error?: string
}

type Props = {
  workerId: string
}

function formatUsd(value: number | null | undefined) {
  if (value == null) return null
  if (value < 0.01 && value > 0) return '<$0.01'
  return `$${value.toFixed(value >= 100 ? 0 : 2)}`
}

function formatResetAt(iso: string | null | undefined) {
  if (!iso) return null
  try {
    const date = new Date(iso)
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    if (diffMs <= 0) return 'agora'
    const hours = Math.floor(diffMs / (1000 * 60 * 60))
    if (hours < 24) return `em ${hours}h`
    const days = Math.floor(hours / 24)
    return `em ${days}d`
  } catch {
    return null
  }
}

export function SpendWidget({ workerId }: Props) {
  const [loading, setLoading] = useState(true)
  const [info, setInfo] = useState<KeyInfo | null>(null)

  const load = () => {
    setLoading(true)
    fetch(`/cc/instances/${workerId}/litellm_key_info`, { headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => setInfo(data))
      .catch((err) => setInfo({ ok: false, error: String(err) }))
      .finally(() => setLoading(false))
  }

  useEffect(load, [workerId])

  if (loading && !info) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        Carregando spend...
      </div>
    )
  }

  if (!info?.ok) {
    return (
      <div className="rounded-lg border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
        Spend indisponível{info?.error ? `: ${info.error}` : ''}.
        <Button type="button" size="sm" variant="ghost" onClick={load} className="ml-2 h-6 gap-1 px-2 text-xs">
          <RefreshCw className="h-3 w-3" /> Tentar de novo
        </Button>
      </div>
    )
  }

  const spend = info.spend ?? 0
  const max = info.max_budget
  const pct = max && max > 0 ? Math.min(100, (spend / max) * 100) : null
  const tone = pct == null ? 'neutral' : pct > 90 ? 'critical' : pct > 70 ? 'warning' : 'ok'

  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Spend LiteLLM</div>
        </div>
        <div className="flex items-center gap-2">
          {info.blocked && (
            <Badge variant="critical" className="gap-1 font-normal">
              <AlertOctagon className="h-3 w-3" /> blocked
            </Badge>
          )}
          <Button type="button" size="icon" variant="ghost" onClick={load} className="h-6 w-6" aria-label="Recarregar spend">
            <RefreshCw className={cn('h-3 w-3', loading && 'animate-spin')} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-baseline gap-3">
        <div className="font-mono text-lg text-foreground">{formatUsd(spend)}</div>
        {max != null && (
          <div className="text-xs text-muted-foreground">
            de <span className="font-mono">{formatUsd(max)}</span>
            {info.budget_duration && <span className="ml-1">/ {info.budget_duration}</span>}
          </div>
        )}
      </div>

      {pct != null && (
        <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-border">
          <div
            className={cn(
              'h-full transition-[width]',
              tone === 'critical' && 'bg-red-500',
              tone === 'warning' && 'bg-amber-500',
              tone === 'ok' && 'bg-green-500 dark:bg-green-400',
            )}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}

      {info.budget_reset_at && (
        <div className="mt-2 text-xs text-muted-foreground">
          Reset {formatResetAt(info.budget_reset_at)}
        </div>
      )}
    </div>
  )
}
